/**
 * YAML ↔ JSON conversion, with the findings the widget reports: where a parse failed,
 * what was converted, and what a reader should double-check.
 */

import {
  DataError,
  type DataValue,
  type DataWarning,
  isMap,
  type JsonIndent,
  stringifyJson,
} from "@/lib/tool-kit/latex-dev/data-model";
import { parseJson } from "@/lib/tool-kit/latex-dev/json-parse";
import {
  DEFAULT_EMIT_OPTIONS,
  emitYaml,
  type YamlEmitOptions,
} from "@/lib/tool-kit/latex-dev/yaml-emit";
import { parseYaml } from "@/lib/tool-kit/latex-dev/yaml-parse";

export type Direction = "yamlToJson" | "jsonToYaml";

export interface ConvertOptions {
  jsonIndent: JsonIndent;
  yamlIndent: 2 | 4;
  blockScalars: boolean;
  merge: boolean;
}

export const DEFAULT_CONVERT_OPTIONS: ConvertOptions = {
  jsonIndent: "2",
  yamlIndent: 2,
  blockScalars: DEFAULT_EMIT_OPTIONS.blockScalars,
  merge: true,
};

export interface Summary {
  type: "mapping" | "sequence" | "scalar";
  /** Entries at the top level (keys or items); 0 for a scalar. */
  entries: number;
  /** Levels of nesting: 1 for a flat mapping. */
  depth: number;
  /** Mapping keys anywhere in the document. */
  keys: number;
}

export interface Problem {
  code: string;
  params: Readonly<Record<string, string | number>>;
  line: number;
  column: number;
  /** The source line the problem is on, cut to a readable length. */
  excerpt: string;
}

export type Outcome =
  | {
      ok: true;
      text: string;
      summary: Summary;
      warnings: DataWarning[];
      /** More than one YAML document was found, so the JSON is an array of them. */
      documents: number;
      /** Only comments or white space: nothing to convert. */
      empty: boolean;
    }
  | { ok: false; problem: Problem };

const OUTPUT_LIMIT = 5_000_000;
const EXCERPT_LIMIT = 120;
const RE_LINE_BREAK = /\r\n?|\n/;

function summarize(value: DataValue): Summary {
  let keys = 0;
  const deepest = (nodes: Iterable<DataValue>): number => {
    let max = 0;
    for (const child of nodes) {
      max = Math.max(max, depth(child));
    }
    return max;
  };
  const depth = (node: DataValue): number => {
    if (Array.isArray(node)) {
      return 1 + deepest(node);
    }
    if (isMap(node)) {
      keys += node.size;
      return 1 + deepest(node.values());
    }
    return 0;
  };
  const levels = depth(value);
  if (Array.isArray(value)) {
    return { type: "sequence", entries: value.length, depth: levels, keys };
  }
  if (isMap(value)) {
    return { type: "mapping", entries: value.size, depth: levels, keys };
  }
  return { type: "scalar", entries: 0, depth: 0, keys };
}

function excerptOf(source: string, line: number): string {
  const text = source.split(RE_LINE_BREAK)[line - 1] ?? "";
  return text.length > EXCERPT_LIMIT
    ? `${text.slice(0, EXCERPT_LIMIT)}…`
    : text;
}

function problemFrom(error: unknown, source: string): Problem {
  if (error instanceof DataError) {
    return {
      code: error.code,
      params: error.params,
      line: error.line,
      column: error.column,
      excerpt: error.line > 0 ? excerptOf(source, error.line) : "",
    };
  }
  // A call stack that is too deep to read: report it as nesting, never crash the page.
  const tooDeep = error instanceof RangeError;
  return {
    code: tooDeep ? "tooDeep" : "internal",
    params: {},
    line: 0,
    column: 0,
    excerpt: "",
  };
}

/** Convert YAML to JSON text. Several documents become an array. */
export function yamlToJson(source: string, options: ConvertOptions): Outcome {
  try {
    const { documents, warnings } = parseYaml(source, { merge: options.merge });
    const value: DataValue = documents.length === 1 ? documents[0] : documents;
    const text = stringifyJson(value, options.jsonIndent, OUTPUT_LIMIT);
    return {
      ok: true,
      text,
      summary: summarize(value),
      warnings,
      documents: documents.length,
      empty: documents.length === 0,
    };
  } catch (error) {
    return { ok: false, problem: problemFrom(error, source) };
  }
}

/** Convert JSON to YAML text. */
export function jsonToYaml(source: string, options: ConvertOptions): Outcome {
  try {
    const { value, warnings } = parseJson(source);
    const emit: YamlEmitOptions = {
      indent: options.yamlIndent,
      blockScalars: options.blockScalars,
    };
    const text = emitYaml(value, emit);
    return {
      ok: true,
      text,
      summary: summarize(value),
      warnings,
      documents: 1,
      empty: false,
    };
  } catch (error) {
    return { ok: false, problem: problemFrom(error, source) };
  }
}
